/**
 * Client wiring for the /guides/subtitle-reading-speed/ CPS checker widget
 * (cue-forge-kjc.3). Imports ONLY the pure `readingSpeed.ts` module — no
 * framework, no core import — so this stays a few KB and is referenced from
 * that one guide page alone (`render.ts` emits its `<script type="module">`
 * tag only when a guide's content carries a `cps-checker` widget), never from
 * the app entry, so it can't affect the eager /app/ bundle budget.
 *
 * Progressive enhancement: the widget's form markup is emitted with the
 * `hidden` attribute (render.ts) so a JS-off reader sees only the static
 * threshold table; `init()` reads the embedded `#cps-thresholds` JSON, binds
 * the inputs, and un-hides the form. `init()` is idempotent (safe to call more
 * than once, and a no-op when the page has no widget markup) so it can be
 * both the module's own on-load call and a test's explicit call after
 * injecting DOM.
 */
import { evaluateReadingSpeed, type CpsWidgetData } from './readingSpeed';

const IDS = {
  form: 'cps-widget-form',
  json: 'cps-thresholds',
  text: 'cps-widget-text',
  duration: 'cps-widget-duration',
  cps: 'cps-widget-cps',
  wpm: 'cps-widget-wpm',
  verdicts: 'cps-widget-verdicts',
} as const;

function readThresholds(doc: Document): CpsWidgetData | undefined {
  const el = doc.getElementById(IDS.json);
  if (!el || !el.textContent) return undefined;
  try {
    return JSON.parse(el.textContent) as CpsWidgetData;
  } catch {
    return undefined;
  }
}

function formatCps(n: number): string {
  return n.toFixed(1);
}

function formatWpm(n: number): string {
  return Math.round(n).toString();
}

/**
 * Bind the widget in `doc` (defaults to the real `document`). A no-op when
 * the page has no widget markup or the widget is already bound, so it is safe
 * to call unconditionally on load and again from a test.
 */
export function init(doc: Document = document): void {
  const form = doc.getElementById(IDS.form);
  const data = readThresholds(doc);
  const textEl = doc.getElementById(IDS.text) as HTMLTextAreaElement | null;
  const durationEl = doc.getElementById(IDS.duration) as HTMLInputElement | null;
  const cpsEl = doc.getElementById(IDS.cps);
  const wpmEl = doc.getElementById(IDS.wpm);
  const verdictsEl = doc.getElementById(IDS.verdicts);
  if (!form || !data || !textEl || !durationEl || !cpsEl || !wpmEl || !verdictsEl) return;
  if (form.dataset.cpsInit === '1') return;
  form.dataset.cpsInit = '1';

  function update(): void {
    const text = textEl!.value;
    const seconds = Number.parseFloat(durationEl!.value);
    const result = evaluateReadingSpeed(text, Number.isFinite(seconds) ? seconds : 0, data!);
    cpsEl!.textContent = formatCps(result.cps);
    wpmEl!.textContent = formatWpm(result.wpm);
    verdictsEl!.replaceChildren(
      ...result.verdicts.map((v) => {
        const li = doc.createElement('li');
        li.className = v.pass ? 'lp-cps__pass' : 'lp-cps__over';
        li.textContent = `${v.label}: ${formatCps(v.cps)} cps (max ${v.maxCps}): ${
          v.pass ? 'Pass' : 'Over'
        }`;
        return li;
      }),
    );
  }

  textEl.addEventListener('input', update);
  durationEl.addEventListener('input', update);
  update();
  form.hidden = false;
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
}
